import { Injectable, inject, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { createAuthCodeFlowConfig } from './auth.config';
import { UserRole } from './auth.role';
import { Router } from '@angular/router';
import { RoleService } from '../role/role.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { AuthService } from './auth.service';
import { environment } from '@htx/environment';
import { HttpClient } from '@angular/common/http';

/**
 * Production authentication service using OAuth2 / OAuthService.
 * Implements AuthService for production authentication flow.
 */
@Injectable()
export class ProdAuthService extends AuthService implements OnDestroy {
  private oauthService = inject(OAuthService);
  private router = inject(Router);
  private roleService = inject(RoleService);
  private httpClient = inject(HttpClient);

  private eventsSub?: Subscription;

  override readonly isLoggedIn$ = new BehaviorSubject<boolean>(
    this.oauthService.hasValidAccessToken()
  );
  override readonly isAdmin$ = new BehaviorSubject<boolean>(false);
  override readonly userRoles$ = new BehaviorSubject<string[]>([]);
  override readonly displayName$ = new BehaviorSubject<string>('');

  constructor() {
    super();
  }

  override getIsLoggedIn$(): BehaviorSubject<boolean> {
    return this.isLoggedIn$;
  }

  override getIsAdmin$(): Observable<boolean> {
    return this.isAdmin$;
  }

  override hasValidAccessToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  override getAccessToken(): string | null {
    return this.oauthService.getAccessToken();
  }

  override init(): void {
    const authCodeFlowConfig = createAuthCodeFlowConfig(environment);
    console.log('OAuth init', JSON.stringify(authCodeFlowConfig));
    this.oauthService.configure(authCodeFlowConfig);
    this.oauthService.setStorage(localStorage);

    this.oauthService.setupAutomaticSilentRefresh();

    this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      this.handleOAuthLoginSuccess();
    });
  }

  override login(): void {
    if (!this.oauthService.hasValidAccessToken()) {
      this.oauthService.initCodeFlow();
    }
  }

  override logout(): void {
    this.oauthService.logOut();
    this.isLoggedIn$.next(false);
    this.isAdmin$.next(false);
    this.roleService.updateRolesFromToken();
    this.router.navigate(['/']);
  }

  // Private methods

  private handleOAuthLoginSuccess(): void {
    const hasValidToken = this.oauthService.hasValidAccessToken();
    this.isLoggedIn$.next(hasValidToken);

    if (hasValidToken) {
      this.roleService.updateRolesFromToken();
      this.updateAdminStatus();
      this.updateDisplayName();
    } else {
      this.isAdmin$.next(false);
      this.userRoles$.next([]);
    }

    this.setupOAuthEventListeners();
  }

  private setupOAuthEventListeners(): void {
    if (this.eventsSub) return;

    this.eventsSub = this.oauthService.events.subscribe((event) => {
      if (event.type === 'token_received') {
        this.roleService.updateRolesFromToken();
        this.updateAdminStatus();
        this.isLoggedIn$.next(true);
      } else if (event.type === 'session_terminated' || event.type === 'session_error') {
        this.logout();
      }
    });
  }

  private updateAdminStatus(): void {
    const roles = this.roleService.getUserRoles$().value;
    const hasAdminRole =
      roles.includes(UserRole.OrganizationAdmin) || roles.includes(UserRole.DataspaceAuthority);
    console.log('Updating admin status. Roles:', roles, 'Has admin:', hasAdminRole);
    this.isAdmin$.next(hasAdminRole);
    // Sync roles to authService userRoles$
    this.userRoles$.next(roles);
  }

  private updateDisplayName(): void {
    this.httpClient
      .get<{ firstName: string; lastName: string }>(`${environment.backendUrl}/members/me`)
      .subscribe({
        next: (member) => {
          const full = [member.firstName, member.lastName].filter(Boolean).join(' ');
          if (full) {
            this.displayName$.next(full);
            return;
          }
          this.updateDisplayNameFromToken();
        },
        error: () => {
          this.updateDisplayNameFromToken();
        }
      });
  }

  private updateDisplayNameFromToken(): void {
    const claims = this.oauthService.getIdentityClaims() as Record<string, string> | null;
    if (claims) {
      const given = claims['given_name'] || '';
      const family = claims['family_name'] || '';
      const full = [given, family].filter(Boolean).join(' ');
      this.displayName$.next(full || claims['preferred_username'] || claims['name'] || '');
    }
  }

  ngOnDestroy(): void {
    this.eventsSub?.unsubscribe();
  }
}
